import { logger } from '@adornis/base/logging.js';
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import type { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import type { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { LogistikProductCategory } from '../enums/LogistikEnums.js';
import { LogistikOrderFilter } from '../filter/LogistikOrderFilter.js';
import { LogistikSettings } from '../LogistikClearanceSettings.js';
import {
  LogistikLearningMaterialOrderItem,
  LogistikOrder,
  LogistikOrderItem,
  ProductSetOrderItem,
  validateCustomerEmail,
} from '../LogistikOrder.js';
import { AdvertisingMaterial, LearningMaterial, LogistikProduct, LogistikProductSet } from '../LogistikProduct.js';
import { Partner } from '../master-data-management/partner/entities/Partner.js';
import type { ChancenwerkUserAspect } from '../master-data-management/users/entities/aspects/ChancenwerkUserAspect.js';
import type { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';
import { KPFPublicInformationAPI } from '../public-access/api/KPFPublicInformationAPI.js';

const placeLogistikOrder = registerMutation({
  type: () => String,
  operationName: 'placeLogistikOrder',
  params: [{ type: () => LogistikOrder, name: 'order' }],
  resolve: (order: LogistikOrder) => {
    return async (gqlFields: void) => {
      await assertValidKpfForOrder(order);
      order = await unrollSetOrder(order);
      validateCustomerEmail(order.shippingDetails.customerEmail, order.isManuallyCreated, order.shippingDetails);

      if (order.items.length === 0) throw new Error('Es muss mindestens ein Produkt bestellt werden');
      const promises = order.items.map(async item => {
        if (!(item instanceof LogistikOrderItem)) return new Promise<void>(resolve => resolve());
        if (item.quantity <= 0) throw new Error(`Die Menge des Produkts ${item.productID} muss größer als 0 sein`);
        await assertValidProductAndOrderAmountForOrderItem(item, order);
      });
      await Promise.all(promises);
      return order.create();
    };
  },
});

async function assertValidKpfForOrder(order) {
  if (!order.shippingDetails.has('kpfID')) return;
  const kpfID = order.shippingDetails.kpfID;

  const kpf = kpfID
    ? await KPFPublicInformationAPI.getByID([kpfID])({ produkt: { name: 1 } }).then(kpfs => kpfs[0])
    : undefined;

  if (!kpf) throw new Error('KPF konnte nicht gefunden werden. Bitte versuchen Sie es später erneut.');

  if (
    projectConfig.get('SERVICE_NAME').toLowerCase() === 'chancenlogistik' &&
    kpf.produkt?.name?.toLowerCase() === 'chancenhefte' &&
    !order.isManuallyCreated
  ) {
    throw new Error('Innerhalb von CHANCENlogistik dürfen keine CHANCENhefte bestellt werden.');
  } else if (
    projectConfig.get('SERVICE_NAME').toLowerCase() === 'chancenhefte' &&
    kpf.produkt?.name?.toLowerCase() !== 'chancenhefte' &&
    !order.isManuallyCreated
  ) {
    throw new Error('Innerhalb von CHANCENhefte dürfen nur CHANCENhefte bestellt werden.');
  }
}

async function unrollSetOrder(order: LogistikOrder): Promise<LogistikOrder> {
  const nonSetItems = order.items.filter(item => !(item instanceof ProductSetOrderItem));
  // @ts-expect-error we just filtered anything else out
  const setItems: ProductSetOrderItem[] = order.items.filter(item => item._class === ProductSetOrderItem._class);

  const orders = await Promise.all(setItems.map(item => unrollSetOrderItem(item))).then(a => a.flat());
  order.items = nonSetItems.concat(orders);
  return order;
}

async function unrollSetOrderItem(item: ProductSetOrderItem): Promise<LogistikOrderItem[]> {
  const setProduct = await getByID<typeof LogistikProductSet>(
    LogistikProductSet,
    item.productID,
  )({ selectedProducts: { _id: 1, productID: 1, quantity: 1 } });
  if (!setProduct) return [];
  const products = await getByID<typeof LogistikProduct>(
    LogistikProduct,
    setProduct.selectedProducts.map(a => a.productID),
  )({ _class: 1, _id: 1 });
  if (!products?.length) return [];
  const orderItems = products.map(product => {
    const quantity =
      item.quantity * (setProduct.selectedProducts.find(a => a.productID === product._id)?.quantity ?? 1);
    switch (product._class) {
      case LearningMaterial._class:
        return new LogistikLearningMaterialOrderItem({
          productID: product._id,
          quantity,
          productCategory: LogistikProductCategory.LEARNING_MATERIAL,
        });
      case AdvertisingMaterial._class:
        return new LogistikOrderItem({
          productID: product._id,
          quantity,
          productCategory: LogistikProductCategory.ADVERTISING_MATERIAL,
        });
      default:
        throw new Error(`Unsupported product type ${product._class}`);
    }
  });
  return orderItems;
}

async function assertValidProductAndOrderAmountForOrderItem(item: LogistikOrderItem, order: LogistikOrder) {
  const col = await getRawCollection<LogistikProduct>(LogistikProduct._collectionName);
  const product = await col.findOne({ _id: item.productID });
  if (!product) throw new Error('Product not found');
  if (context.serverContext) throw new Error('Server kann keine Produkte bestellen');
  const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({ email: 1 });
  const settings = await LogistikSettings.get()(LogistikSettings.allFields);

  if ((!user || !settings.noMaxOrderAmountEmails.includes(user.email)) && !order.isManuallyCreated) {
    if (item.quantity > product.maxOrderAmount || item.quantity > product.availableQuantity)
      throw new Error(`Die Menge des Produkts ${product.title} übersteigt die maximal bestellbare Menge`);
  }
  if (item.quantity < product.minOrderAmount && !order.isManuallyCreated)
    throw new Error(`Die Menge des Produkts ${product.title} unterschreitet die minimale bestellbare Menge`);
}

const subscribeLogistikOrdersPaginated = registerSubscription({
  type: () => [LogistikOrder],
  operationName: 'subscribeLogistikOrdersPaginated',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => LogistikOrderFilter, name: 'filter' },
  ],
  resolve: (skip: number, limit: number, filter: Maybe<LogistikOrderFilter>) => {
    return (gqlFields: BaseQLSelectionSet<LogistikOrder>) => {
      if (!filter) return of([] as LogistikOrder[]);

      return combineLatest([publishFilter(LogistikOrder._class), filter.getMongoFilter()]).pipe(
        catchError(e => {
          logger.error(e);
          return of([]);
        }),
        switchMap(([publishFilter, mongoFilter]) => {
          const basicSelector = publishFilter ? { $and: [publishFilter, mongoFilter] } : mongoFilter;
          const options = { projection: { _id: 1 } };
          return watchQuery(LogistikOrder._collectionName, basicSelector, options).pipe(map(() => basicSelector));
        }),
        switchMap(async selector => {
          return (await getRawCollection<LogistikOrder>(LogistikOrder._collectionName))
            .aggregate<LogistikOrder>(constructPaginatedLogistikOrdersAggregation(skip, limit, filter, selector))
            .toArray()
            .catch(err => {
              logger.error({ err }, 'Fehler in der Aggregation in subscribeLogistikOrdersPaginated');
              logger.info(filter.getMongoOptions());
              return [] as LogistikOrder[];
            });
        }),
        catchError(err => {
          logger.error({ err }, 'Fehler in subscribeLogistikOrdersPaginated');
          return of([] as LogistikOrder[]);
        }),
      );
    };
  },
});

function constructPaginatedLogistikOrdersAggregation(
  skip: number,
  limit: number,
  filter: LogistikOrderFilter,
  selector: Dictionary<any>,
) {
  return [
    ...convertFilterToAggregationPhases(filter, selector),
    { $sort: filter.getMongoOptions().sort },
    { $skip: skip },
    { $limit: limit },
  ];
}

const attachPartnerToLogistikOrder = [
  {
    $lookup: {
      from: Partner._collectionName,
      localField: 'shippingDetails.shippingSchoolID',
      foreignField: '_id',
      as: 'partner',
      pipeline: [{ $project: { name: 1 } }],
    },
  },
];

function convertFilterToAggregationPhases(filter: LogistikOrderFilter, selector: Dictionary<any>) {
  const clientFilter = filter.getFilterAspect<StringAspect>('client');
  const statusFilter = filter.getFilterAspect<ListAspect>('status')?.getMongoFilter();
  const productCategoryFilter = filter.getFilterAspect<ListAspect>('productCategory');
  const processorFilter = filter.getFilterAspect<ChancenwerkUserAspect>('bearbeiter');
  const auftraggeberFilter = filter.getFilterAspect<StringAspect>('auftraggeber');
  const isManuallyCreatedFilter = filter.getFilterAspect<BooleanAspect>('isManuallyCreated');
  const orderNumberFilter = filter.getFilterAspect<StringAspect>('orderNumber');
  const empfaengerFilter = filter.getFilterAspect<ListAspect>('empfaenger');
  const tokenFilter = filter.getFilterAspect<StringAspect>('accessToken');

  return [
    ...(clientFilter?.value ? attachPartnerToLogistikOrder : []),
    {
      $match: {
        $and: [
          selector,
          ...(clientFilter?.value ? [{ 'partner.name': { $regex: clientFilter.value, $options: 'i' } }] : []),
          ...(statusFilter ? [statusFilter] : []),
          ...constructProductCategorySelector(productCategoryFilter),
          ...(orderNumberFilter?.value ? [{ orderNumber: { $regex: orderNumberFilter.value, $options: 'i' } }] : []),
          ...(processorFilter?.value?.length ? [{ processorID: { $in: processorFilter.value } }] : []),
          ...constructAuftraggeberSelector(auftraggeberFilter),
          ...(isManuallyCreatedFilter?.value ? [{ isManuallyCreated: isManuallyCreatedFilter.value }] : []),
          ...constructEmpfaengerSelector(empfaengerFilter),
          ...constructTokenSelector(tokenFilter),
        ],
      },
    },
  ];
}

function constructAuftraggeberSelector(aspect: Maybe<StringAspect>) {
  if (!aspect?.value) return [];
  return [
    {
      $or: [
        { 'shippingDetails.customerEmail': { $regex: aspect.value, $options: 'i' } },
        { 'shippingDetails.shippingRecipientName': { $regex: aspect.value, $options: 'i' } },
      ],
    },
  ];
}

function constructProductCategorySelector(aspect: Maybe<ListAspect>) {
  const selectedCategories = aspect?.values;
  if (!selectedCategories?.length || !aspect) return [];

  switch (aspect.mode) {
    case 'containsAND':
      return [{ items: { $all: selectedCategories.map(a => ({ $elemMatch: { productCategory: a } })) } }];
    case 'containsNONE':
      return [{ items: { $not: { $elemMatch: { productCategory: { $in: selectedCategories } } } } }];
    case 'empty':
      return [{ items: { $eq: [] } }];
    default:
      return [{ items: { $elemMatch: { productCategory: { $in: selectedCategories } } } }];
  }
}

function constructEmpfaengerSelector(aspect: Maybe<ListAspect>) {
  const selectedValues = aspect?.values;
  if (!selectedValues?.length || !aspect) return [];

  switch (aspect.mode) {
    case 'containsAND':
      return [{ $and: selectedValues.map(a => ({ 'shippingDetails.shippingRecipient': a })) }];
    case 'containsNONE':
      return [{ $and: selectedValues.map(a => ({ 'shippingDetails.shippingRecipient': { $ne: a } })) }];
    case 'empty':
      return [{ 'shippingDetails.shippingRecipient': { $exists: false } }];
    case 'containsOR':
      return [{ $or: selectedValues.map(a => ({ 'shippingDetails.shippingRecipient': a })) }];
    default:
      throw new Error('unsupported list aspect mode');
  }
}

function constructTokenSelector(aspect: Maybe<StringAspect>) {
  if (!aspect?.value) return [];
  return [{ $expr: { $regexMatch: { input: { $toString: '$accessToken' }, regex: aspect.value, options: 'i' } } }];
}

export namespace LogistikOrderAPI {
  export const placeOrder = placeLogistikOrder;
  export const subscribePaginated = subscribeLogistikOrdersPaginated;
}
